import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { AUDIENCE_CSV_IMPORT_TYPE } from '@/constants/audiences';
import {
  copyWorkflow,
  createAudience,
  deleteAllRowsByListId,
  deleteAudience,
  duplicateAudience,
  getAllAudiences,
  getCampaignTemplates,
  getAllFacts,
  getAudienceById,
  getAudienceCountries,
  getAudienceContinents,
  getAudienceStates,
  getAudienceCsvOrImagesDataUploaded,
  getAudienceAttributeColumns,
  getAudienceRowsByIds,
  getAudienceMatches,
  getAvailableWorkflows,
  getSystemWorkflows,
  searchAudiences,
  updateAudience,
  uploadCsvForAudience,
  provisionAudienceWebhook,
  getAudienceWebhookConfig,
  getAudienceWebhookActivity,
  getAudienceWebhookDetectedFields,
  updateAudienceWebhookFilter,
  deleteAudienceWebhook,
  crmEnrichAudience,
  getEnrichSummary,
  getAudienceTags,
  getRecommendedAudiences,
  clearCampaignAttention,
} from '@/services/audiences';
import type { EnrichableField } from '@/lib/enrichment';
import { copyWorkflowWithAudience } from '@/services/plays';
import type { CopyWorkflowWithAudienceRequest } from '@/services/plays';
import { ICreateAudienceBody, IUpdateAudienceBody } from '@/types/audiences';
import {
  keepPreviousData,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useState } from 'react';
import { apiInstance } from '@/utils';
import { REQUEST_METHOD } from '@/constants/common';
import { useAuth } from '@/contexts';
import type { SortState } from '@/components/shared/DataTable';

export const useGetAllFacts = (enabled = true) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getAllFacts],
    queryFn: getAllFacts,
    enabled: enabled && isAuthenticated, // Only run when authenticated AND explicitly enabled
  });
};

export const useCreateAudience = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (data: any) => {
    const resp = await createAudience(data);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getAllAudiences],
    });

    return resp;
  };

  return useMutation({
    mutationFn,
  });
};

export type GetAllAudiencesParams = {
  page?: number;
  limit?: number;
  type?: 'company' | 'person';
  creation_method?: string[];
  workflow_status?: 'has_workflows' | 'no_workflows';
  published_status?: 'published' | 'not_published';
  trigger_type?: 'manual' | 'webhook';
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  owner_user_id?: string;
  view?: 'campaign_picker' | 'audience_picker' | 'power_dialer_picker';
  // Campaigns Gallery "Team" scope + tag-pill filter
  shared_with_team?: boolean;
  tags?: string[];
};

export const useGetAllAudiences = (
  params?: GetAllAudiencesParams,
  options?: { enabled?: boolean },
) => {
  const { isAuthenticated } = useAuth();
  const queryEnabled = options?.enabled ?? true;

  return useQuery({
    queryKey: [API_ENDPOINTS.getAllAudiences, params],
    queryFn: () => getAllAudiences(params),
    enabled: queryEnabled && isAuthenticated,
    placeholderData: keepPreviousData,
  });
};

type AudienceListPage = { audiences?: Array<{ id: number; attention_cleared_failed_steps?: number | null }> };

export const CLEAR_CAMPAIGN_ATTENTION_KEY = ['clear-campaign-attention'] as const;

export interface ClearCampaignAttentionVariables {
  audienceId: number;
  /** The failed-step count to clear at, or null to restore the mark. */
  failedSteps: number | null;
  /** The row's `execution_stats.last_failure_at` as the rep saw it. */
  lastFailureAt: string | null;
  /** What the row held before, put back if the server refuses. */
  previous: number | null;
}

/**
 * Clear (a count) or restore (`null`) a campaign's "Needs attention" mark. Every cached
 * Campaigns page is patched first, so the row leaves its group the moment the rep clicks,
 * and put back if the server refuses. The caller runs one write per campaign at a time
 * (`pendingIds`), so a rollback never overwrites a newer write to the same row.
 */
export const useClearCampaignAttention = () => {
  const queryClient = useQueryClient();
  const listKey = [API_ENDPOINTS.getAllAudiences];
  const patch = (audienceId: number, failedSteps: number | null) => {
    queryClient.setQueriesData<AudienceListPage>({ queryKey: listKey }, (page) => (
      page?.audiences
        ? {
          ...page,
          audiences: page.audiences.map((row) => (
            row.id === audienceId ? { ...row, attention_cleared_failed_steps: failedSteps } : row
          )),
        }
        : page
    ));
  };

  const mutation = useMutation({
    mutationKey: CLEAR_CAMPAIGN_ATTENTION_KEY,
    mutationFn: ({ audienceId, failedSteps, lastFailureAt }: ClearCampaignAttentionVariables) =>
      clearCampaignAttention(audienceId, failedSteps, lastFailureAt),
    onMutate: async ({ audienceId, failedSteps }) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      patch(audienceId, failedSteps);
    },
    onError: (_error, { audienceId, previous }) => patch(audienceId, previous),
    // Refetch once the last write lands: a refetch started while another campaign's write is
    // in flight could carry that row's old state over its optimistic patch.
    onSettled: () => (
      queryClient.isMutating({ mutationKey: CLEAR_CAMPAIGN_ATTENTION_KEY }) <= 1
        ? queryClient.invalidateQueries({ queryKey: listKey })
        : undefined
    ),
  });
  const pendingIds = new Set(useMutationState({
    filters: { mutationKey: CLEAR_CAMPAIGN_ATTENTION_KEY, status: 'pending' },
    select: (pending) => (pending.state.variables as ClearCampaignAttentionVariables | undefined)?.audienceId,
  }).filter((id): id is number => id !== undefined));
  return { ...mutation, pendingIds };
};

export const useGetAudienceTags = (scope: 'mine' | 'team') => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [API_ENDPOINTS.getAudienceTags, scope],
    queryFn: () => getAudienceTags(scope),
    enabled: isAuthenticated,
  });
};

// Campaign Wizard step 0 "Recommended" row (backlog #9). Fires independently
// of the paginated audience table, non-blocking — a failed/slow response
// just means no Recommended row renders, never an error the picker itself
// shows (matches every other wizard AI-adjacent call's fail-soft contract).
export const useGetRecommendedAudiences = () => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [API_ENDPOINTS.getRecommendedAudiences],
    queryFn: getRecommendedAudiences,
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
};

export const useDeleteAudience = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (id: number) => {
    const resp = await deleteAudience(id);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getAllAudiences],
    });

    return resp;
  };

  return useMutation({
    mutationFn,
  });
};

export const useUpdateAudience = () => {
  const queryClient = useQueryClient();

  const mutationFn = async ({ id, data }: { id: number; data: IUpdateAudienceBody }) => {
    const resp = await updateAudience(id, data);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getAllAudiences],
    });

    return resp;
  };

  return useMutation({
    mutationFn,
  });
};

export const useSearchAudiences = () => {
  return useMutation({
    mutationFn: searchAudiences,
  });
};

export const useUploadCsvForAudience = () => {
  return useMutation({
    mutationFn: uploadCsvForAudience,
  });
};

export const useGetAudienceCsvOrImagesDataUploaded = (
  type: AUDIENCE_CSV_IMPORT_TYPE,
  list_id: number,
) => {
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 25,
  });

  return {
    ...useQuery({
      queryFn: () => getAudienceCsvOrImagesDataUploaded({ type, list_id, params: queryParams }),
      queryKey: [
        API_ENDPOINTS.getAudienceCsvOrImagesDataUploaded(type, list_id),
        type,
        list_id,
        queryParams,
      ],
      enabled: !!type && !!list_id,
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60 * 2,
    }),
    queryParams,
    setQueryParams,
  };
};

export const useDeleteAllRowsByListId = () => {
  return useMutation({
    mutationFn: deleteAllRowsByListId,
  });
};

export const useGetAudienceById = (id: number) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getAudienceById(id), id],
    queryFn: () => getAudienceById(id),
    enabled: !!id,
  });
};

export const useDuplicateAudience = () => {
  const queryClient = useQueryClient();
  const mutationFn = async (data: { audienceId: number; name: string }) => {
    const resp = await duplicateAudience(data);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getAllAudiences],
    });
    return resp;
  };
  return useMutation({
    mutationFn,
  });
};

export const useGetAudienceCountries = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getAudienceCountries],
    queryFn: getAudienceCountries,
    enabled: isAuthenticated, // Only run when authenticated
  });
};

export const useGetAudienceContinents = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getAudienceContinents],
    queryFn: getAudienceContinents,
    enabled: isAuthenticated, // Only run when authenticated
  });
};

export const useGetAudienceStates = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getAudienceStates],
    queryFn: getAudienceStates,
    enabled: isAuthenticated, // Only run when authenticated
  });
};

export const useGetAvailableWorkflows = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getAvailableWorkflows],
    queryFn: getAvailableWorkflows,
    enabled: isAuthenticated, // Only run when authenticated
  });
};

/**
 * useGetSystemWorkflows - Fetch all system workflow templates
 * 
 * API ENDPOINT: GET /private/plays/system-workflows
 * 
 * BACKEND QUERY:
 * ```sql
 * SELECT * FROM audiences 
 * WHERE is_system = true 
 * ORDER BY created_at DESC
 * ```
 * 
 * RESPONSE SHAPE:
 * Array of audiences with full configuration including:
 * - id, name, description
 * - creation_method (determines which form shows on edit)
 * - schedule_config (if template uses scheduled trigger)
 * - hubspot_field/value/operator (if template syncs from HubSpot)
 * - rules (if template is rules-based)
 * 
 * AUTHENTICATION:
 * - Only runs when isAuthenticated = true
 * - Uses Supabase JWT from auth context
 * - Backend validates JWT and returns all system templates
 * 
 * CACHING:
 * - React Query caches result with key [API_ENDPOINTS.getSystemWorkflows]
 * - Refetches on mount by default
 * - No stale time configured (always fresh)
 * 
 * @returns {UseQueryResult} Query object with system workflows data
 * @example
 * const { data: systemWorkflows, isLoading } = useGetSystemWorkflows();
 */
export const useGetSystemWorkflows = (creationMethod?: string) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getSystemWorkflows, creationMethod],
    queryFn: () => getSystemWorkflows(creationMethod),
    enabled: isAuthenticated, // Only run when authenticated
  });
};

/**
 * useCopyWorkflow - Clone a workflow template to new or existing audience
 * 
 * API ENDPOINT: POST /private/plays/copy/actions
 * 
 * REQUEST BODY (ICopyWorkflowBody):
 * ```typescript
 * {
 *   sourceAudienceId: number;     // Template being cloned
 *   targetAudienceId: number;     // Destination audience (0 for new)
 * }
 * ```
 * 
 * BACKEND OPERATIONS:
 * 1. Fetches source audience (template) with all configuration
 * 2. Validates user has access to both audiences
 * 3. Creates new audience copying ALL relevant fields:
 *    - creation_method (CRITICAL for form routing!)
 *    - schedule_config + timezone (if template is scheduled)
 *    - hubspot_* fields (if template syncs from HubSpot)
 *    - rules (if template is rules-based)
 *    - Sets source_template_id for tracking
 *    - Sets is_system = false (clones are user workflows)
 * 4. Copies all audience_plays (workflow nodes) preserving:
 *    - sequence_order (execution order)
 *    - custom_action_details (AI instructions, email templates)
 *    - play_id (action type: email, AI, CRM, etc.)
 * 5. Returns information about the copied plays
 * 
 * RESPONSE SHAPE (ICopyWorkflowResponse):
 * ```typescript
 * {
 *   message: string; // Summary message about the copy operation
 *   copiedPlays: Array<{
 *     audience_id: number;
 *     // Additional fields describing each copied play
 *   }>;
 * }
 * ```
 * 
 * ERROR HANDLING:
 * - 404: Template not found or user lacks access
 * - 400: Invalid request body
 * - 500: Database error during copy
 * 
 * @returns {UseMutationResult} Mutation object for copying workflows
 * @example
 * const { mutate: copyWorkflow, isPending } = useCopyWorkflow();
 * 
 * copyWorkflow(
 *   { sourceAudienceId: 268, targetAudienceId: 0 },  // 0 = create new
 *   {
 *     onSuccess: (data) => {
 *       toast.success(`Workflow cloned! ${data.copiedPlays.length} plays copied`);
 *       router.push('/audiences/workflow-builder');
 *     },
 *     onError: (error) => {
 *       toast.error(`Failed to clone: ${error.message}`);
 *     }
 *   }
 * );
 */
export const useCopyWorkflow = () => {
  return useMutation({
    mutationFn: copyWorkflow,
  });
};

/**
 * useCopyWorkflowWithAudience - Copy a workflow to a new or existing audience.
 *
 * Used both to apply a saved/system template (which creates the audience) and to duplicate
 * a campaign from the Campaigns list (which may instead point the copy at an audience the
 * operator already has). `audienceMode` decides — see services/plays :: copyWorkflowWithAudience.
 *
 * A copy always moves a workflow, so both the audience list and the play listings can be
 * stale afterwards; this invalidates both. It used to exist twice, once here and once in
 * usePlaysQuery, each invalidating only half of that.
 */
export const useCopyWorkflowWithAudience = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CopyWorkflowWithAudienceRequest) => {
      const resp = await copyWorkflowWithAudience(data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getAllAudiences] }),
        queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getPlayListings] }),
      ]);
      return resp;
    },
  });
};

// ---------------- HubSpot helpers ----------------

export const usePreviewHubSpotContacts = () => {
  return useMutation({
    mutationFn: async ({
      field,
      value,
      operator = 'EQ',
      objectType = 'contacts',
      filterGroups,
    }: {
      field?: string;
      value?: string;
      operator?: string;
      objectType?: string;
      filterGroups?: Array<Record<string, unknown>>;
    }) => {
      if (Array.isArray(filterGroups)) {
        const resp = await apiInstance({
          method: REQUEST_METHOD.POST,
          url: API_ENDPOINTS.previewHubSpotContacts,
          data: {
            object_type: objectType,
            filter_groups: filterGroups,
          },
          timeout: 30000,
        });
        return resp.data?.data;
      }

      console.log('[MUTATION] usePreviewHubSpotContacts called with:', { field, value, operator, objectType });
      const url = `${API_ENDPOINTS.previewHubSpotContacts}?field=${encodeURIComponent(
        field || '',
      )}&value=${encodeURIComponent(value || '')}&operator=${encodeURIComponent(operator)}&object_type=${encodeURIComponent(objectType)}`;
      console.log('[MUTATION] Making request to:', url);

      try {
        const resp = await apiInstance({
          method: REQUEST_METHOD.GET,
          url,
          timeout: 30000, // 30 second timeout to prevent infinite hanging
        });
        console.log('[MUTATION] Response received:', resp.data);
        return resp.data?.data; // uniform with backend response {success,data}
      } catch (error) {
        console.error('[MUTATION] Error in usePreviewHubSpotContacts:', error);
        throw error;
      }
    },
  });
};

const usePreviewAISearchContacts = () => {
  return useMutation({
    mutationFn: async ({ query }: { query: string }) => {
      const resp = await apiInstance({
        method: REQUEST_METHOD.POST,
        url: '/private/ai-search/preview',
        data: { query },
      });
      return resp.data;
    },
  });
};

const useImportAISearchContacts = () => {
  return useMutation({
    mutationFn: async ({ query, limit = 2000 }: { query: string; limit?: number }) => {
      const resp = await apiInstance({
        method: REQUEST_METHOD.POST,
        url: '/private/ai-search/import',
        data: { query, limit },
      });
      return resp.data;
    },
  });
};

export const useFetchHubSpotFields = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['hubspot-fields'],
    queryFn: async () => {
      const resp = await apiInstance({
        method: REQUEST_METHOD.GET,
        url: `${API_ENDPOINTS.getHubspotFields}?type=person`,
      });
      // Backend responds with { success: true, data: { hubspotFields: { contact: [...] } } }
      // Extract the array of contact properties and map to their "name" values.
      const contactFields =
        resp.data?.data?.hubspotFields?.contact || resp.data?.hubspotFields?.contact || [];

      // Ensure we always return an array of strings for easy rendering in HubSpotSearch.
      return Array.isArray(contactFields) ? contactFields.map((f: any) => f.name) : [];
    },
    enabled: isAuthenticated, // Only run when authenticated
  });
};

// ---------------- HubSpot Lists helpers ----------------

export const usePreviewHubSpotListContacts = () => {
  return useMutation({
    mutationFn: async ({ listId, limit = 100 }: { listId: number | string; limit?: number }) => {
      const resp = await apiInstance({
        method: REQUEST_METHOD.GET,
        url: `${API_ENDPOINTS.getHubSpotListContacts(listId)}?limit=${limit}`,
      });
      return resp.data?.data;
    },
  });
};

export const useImportHubSpotListContacts = () =>
  useMutation({
    mutationFn: async ({ listId, limit = 2000 }: { listId: number | string; limit?: number }) => {
      const resp = await apiInstance({
        method: REQUEST_METHOD.POST,
        url: API_ENDPOINTS.importHubSpotListContacts(listId),
        data: { limit },
      });

      // Return the data which should contain the DB listId
      return resp.data?.data ?? resp.data;
    },
  });

const useGetHubSpotAudienceMembers = (type: 'person' | 'company', list_id: number) => {
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 25,
  });

  return {
    ...useQuery({
      queryFn: () =>
        getAudienceCsvOrImagesDataUploaded({
          type: type as AUDIENCE_CSV_IMPORT_TYPE,
          list_id,
          params: queryParams,
        }),
      queryKey: [
        API_ENDPOINTS.getAudienceCsvOrImagesDataUploaded(type as AUDIENCE_CSV_IMPORT_TYPE, list_id),
        type,
        list_id,
        queryParams,
      ],
      enabled: !!list_id,
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60 * 2,
    }),
    queryParams,
    setQueryParams,
  };
};

export const useGetAudienceMembers = (
  type: 'person' | 'company',
  list_id: number,
  page: number = 1,
  limit: number = 25,
  search?: string,
  /**
   * Optional set of row ids to fetch. When provided, the backend returns
   * ALL matching rows in a single response (ignores page/limit/search) —
   * used by the "Only matching" toggle on the Audience Detail Contacts tab
   * after a list-check (customers / overlap / outreach) has been run.
   * Pass `undefined` for the default paginated fetch.
   */
  rowIds?: number[],
  sort?: SortState | null,
  /** #2223: server-side boolean research-column filter (true/false/unanswered). */
  researchFilter?: { columnId: number; value: 'true' | 'false' | 'unanswered' } | null,
  /**
   * Find People grid filter, company audiences only (the caller passes null
   * otherwise). Filtered server-side so the page/count stay correct.
   * - 'found': only rows with an actual discovered contact.
   * - 'hide_not_found': drop confirmed misses (contact_finder_status='not_found')
   *   but keep not-yet-searched placeholders.
   */
  contactFilter?: 'found' | 'hide_not_found' | null,
  /** Hold the fetch until the caller's page/limit inputs are settled. */
  options?: { enabled?: boolean },
) => {
  const trimmed = typeof search === 'string' ? search.trim() : '';
  const searchParam = trimmed.length > 0 ? trimmed : undefined;
  const sortBy = sort?.columnId;
  const sortOrder = sort?.direction || undefined;
  const researchFilterKey = researchFilter ? `${researchFilter.columnId}:${researchFilter.value}` : '';
  const hasRowIds = Array.isArray(rowIds) && rowIds.length > 0;
  // Cache-key signature for the rowIds set — sort + join so two calls with
  // the same ids in different order share the cache. Empty when not filtering.
  const rowIdsKey = hasRowIds
    ? rowIds!.slice().sort((a, b) => a - b).join(',')
    : '';

  return useQuery({
    queryFn: () => {
      // POST /by-ids when filtering by ids — supports thousands of ids in
      // the body where the GET ?row_ids= query string would hit HTTP 431.
      if (hasRowIds) {
        return getAudienceRowsByIds({
          type: type as AUDIENCE_CSV_IMPORT_TYPE,
          list_id,
          ids: rowIds!,
          ...(sortBy && sortOrder ? { sortBy, sortOrder } : {}),
        });
      }
      return getAudienceCsvOrImagesDataUploaded({
        type: type as AUDIENCE_CSV_IMPORT_TYPE,
        list_id,
        params: {
          page,
          limit,
          ...(searchParam ? { search: searchParam } : {}),
          ...(sortBy && sortOrder ? { sortBy, sortOrder } : {}),
          ...(researchFilter ? { researchFilterColumn: researchFilter.columnId, researchFilterValue: researchFilter.value } : {}),
          ...(contactFilter ? { contact_filter: contactFilter } : {}),
        },
      });
    },
    queryKey: [
      hasRowIds
        ? API_ENDPOINTS.getAudienceRowsByIds(type as AUDIENCE_CSV_IMPORT_TYPE, list_id)
        : API_ENDPOINTS.getAudienceCsvOrImagesDataUploaded(type as AUDIENCE_CSV_IMPORT_TYPE, list_id),
      type,
      list_id,
      page,
      limit,
      searchParam ?? '',
      rowIdsKey,
      sortBy ?? '',
      sortOrder ?? '',
      researchFilterKey,
      contactFilter ?? '',
    ],
    enabled: !!type && !!list_id && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 2,
  });
};

export const useGetAudienceAttributeColumns = (
  type: 'person' | 'company',
  list_id: number,
  audience_id: number,
) => useQuery({
  queryFn: () => getAudienceAttributeColumns({
    audience_id,
    type: type as AUDIENCE_CSV_IMPORT_TYPE,
    list_id,
  }),
  queryKey: [
    API_ENDPOINTS.getAudienceAttributeColumns(type as AUDIENCE_CSV_IMPORT_TYPE, list_id),
    type,
    list_id,
    audience_id,
  ],
  enabled: !!type && !!list_id && !!audience_id,
  staleTime: 1000 * 60 * 5,
});

export const useGetAudienceMatches = (
  audienceId: number,
  page: number = 1,
  limit: number = 10,
  search?: string,
  sort?: SortState | null,
  /** Hold the fetch until the caller's page/limit inputs are settled; extra query params
   *  (a loop audience's `loop_state`). */
  options?: { enabled?: boolean; params?: Record<string, string> },
) => {
  const trimmed = typeof search === 'string' ? search.trim() : '';
  const extraParams = options?.params;
  const searchParam = trimmed.length > 0 ? trimmed : undefined;
  const sortBy = sort?.columnId;
  const sortOrder = sort?.direction || undefined;

  return useQuery({
    queryFn: () =>
      getAudienceMatches(audienceId, {
        page,
        limit,
        ...(searchParam ? { search: searchParam } : {}),
        ...(sortBy && sortOrder ? { sortBy, sortOrder } : {}),
        ...extraParams,
      }),
    queryKey: [
      API_ENDPOINTS.getAudienceMatches(audienceId),
      audienceId,
      page,
      limit,
      searchParam ?? '',
      sortBy ?? '',
      sortOrder ?? '',
      extraParams ?? null,
    ],
    enabled: !!audienceId && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 2,
  });
};

/**
 * What test-processing (and run-workflow / run-now) answers with. `messageBody` is the
 * Service Bus payload that was sent; it is absent when nothing was sent. `scheduled_for`
 * is set when a real run on a list campaign starts 2+ minutes out — no run exists until then.
 */
interface WorkflowDispatchResponse {
  message: string;
  messageBody?: { workflowRunId?: string };
  scheduled_for?: string;
}

export const useTestAudienceProcessing = () => {
  return useMutation({
    mutationFn: async (data: { audienceId: number; test?: boolean }): Promise<WorkflowDispatchResponse> => {
      const resp = await apiInstance({
        method: REQUEST_METHOD.POST,
        url: API_ENDPOINTS.testProcessing,
        data,
      });
      return resp.data;
    },
  });
};

const useInfiniteHubSpotLists = (search: string) =>
  useInfiniteQuery({
    queryKey: ['hubspot-lists', search],
    queryFn: ({ pageParam = 1 }) =>
      apiInstance({
        method: REQUEST_METHOD.GET,
        url: API_ENDPOINTS.getHubSpotLists,
        params: { search, page: pageParam, limit: 50 },
      }).then((r) => r.data?.data ?? r.data),
    initialPageParam: 1,
    getNextPageParam: (last: any) => (last?.hasMore ? last.page + 1 : undefined),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10, // Keep data in cache longer
    refetchOnWindowFocus: false, // Reduce unnecessary refetches
    refetchOnMount: false, // Don't refetch on every mount
  });

// Alternative simple lists hook for better performance
export const useHubSpotLists = (search: string, enabled: boolean = true) =>
  useQuery({
    queryKey: ['hubspot-lists-simple', search],
    queryFn: () =>
      apiInstance({
        method: REQUEST_METHOD.GET,
        url: API_ENDPOINTS.getHubSpotLists,
        params: { search, page: 1, limit: 100 }, // Get more items in single request
      }).then((r) => r.data?.data ?? r.data),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    enabled,
  });

// GH#1146: Add contacts to a HubSpot list
export interface AddToHubSpotListContact {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  jobtitle?: string;
  website?: string;
}

export interface AddToHubSpotListResult {
  added: number;
  created: number;
  alreadyInList: number;
  errors: Array<{ email: string | null; reason: string }>;
}

export const useAddContactsToHubSpotList = () =>
  useMutation({
    mutationFn: async ({
      listId,
      contacts,
    }: {
      listId: number | string;
      contacts: AddToHubSpotListContact[];
    }): Promise<AddToHubSpotListResult> => {
      const resp = await apiInstance({
        method: REQUEST_METHOD.POST,
        url: API_ENDPOINTS.addContactsToHubSpotList(listId),
        data: { contacts },
      });
      return resp.data?.data ?? resp.data;
    },
  });

// ─── Audience Webhook Trigger hooks ──────────────────────────────────────────────

export const useProvisionAudienceWebhook = () =>
  useMutation({
    mutationFn: (audienceId: number) => provisionAudienceWebhook(audienceId),
  });

export const useGetAudienceWebhookConfig = (audienceId: number, enabled: boolean) =>
  useQuery({
    queryKey: ['audience-webhook-config', audienceId],
    queryFn: () => getAudienceWebhookConfig(audienceId),
    enabled,
  });

export const useGetAudienceWebhookActivity = (audienceId: number, enabled: boolean) =>
  useQuery({
    queryKey: ['audience-webhook-activity', audienceId],
    queryFn: () => getAudienceWebhookActivity(audienceId),
    enabled,
    refetchInterval: enabled ? 30_000 : false,
  });

export const useGetAudienceWebhookDetectedFields = (audienceId: number, enabled: boolean) =>
  useQuery({
    queryKey: ['audience-webhook-fields', audienceId],
    queryFn: () => getAudienceWebhookDetectedFields(audienceId),
    enabled,
  });

export const useUpdateAudienceWebhookFilter = () =>
  useMutation({
    mutationFn: ({ audienceId, filter }: { audienceId: number; filter: import('@/types/audiences').TriggerFilter | null }) =>
      updateAudienceWebhookFilter(audienceId, filter),
  });

export const useDeleteAudienceWebhook = () =>
  useMutation({
    mutationFn: (audienceId: number) => deleteAudienceWebhook(audienceId),
  });

// Free CRM enrichment (HubSpot/Salesforce mirror) — bulk, set-based, no credits.
export const useCrmEnrichAudience = () =>
  useMutation({
    mutationFn: crmEnrichAudience,
  });

/**
 * Row-scope and per-provider eligibility counts for one enrichable column.
 *
 * Fetched only while the column menu is open (`enabled`) — the counts move whenever an
 * enrichment run lands, and holding them in cache the rest of the time would show a user
 * stale numbers on the one screen where the number IS the decision. Callers invalidate
 * this key after a run so the reopened menu reflects the new attempt log.
 */
export const useGetEnrichSummary = (
  type: 'person' | 'company',
  list_id: number,
  field: EnrichableField,
  options?: { enabled?: boolean; includeProviders?: boolean },
) => {
  // Two shapes, two cache entries: the legend's cheap totals must never satisfy the menu,
  // which would leave every provider looking exhausted.
  const includeProviders = options?.includeProviders !== false;
  return useQuery({
    queryFn: () => getEnrichSummary({
      type: type as AUDIENCE_CSV_IMPORT_TYPE, list_id, field, includeProviders,
    }),
    queryKey: [
      API_ENDPOINTS.getEnrichSummary(type as AUDIENCE_CSV_IMPORT_TYPE, list_id, field),
      type,
      list_id,
      field,
      includeProviders,
    ],
    enabled: (options?.enabled ?? true) && !!list_id,
    staleTime: 0,
  });
};

/** Separate cache contract: never reuse full audience-list or other scope data. */
export const useCampaignTemplates = (scope: 'mine' | 'team', page: number) => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [API_ENDPOINTS.getAllAudiences, 'campaign_picker', scope, page],
    queryFn: ({ signal }) => getCampaignTemplates(scope, page, signal),
    enabled: isAuthenticated,
    retry: false,
  });
};
